import { escapeHtml } from '../utils/escapeHtml';

class BrowserPrintService {
  constructor() {
    this.isPrintSupported = 'print' in window;
    // Uma impressora termica processa uma comanda por vez. A fila tambem
    // impede que um cupom substitua o HTML de outro durante picos de pedidos.
    this.printQueue = Promise.resolve();
  }

  getPrinterConfig(lanchonete) {
    const value = lanchonete?.printer_config;
    if (value && typeof value === 'object') return value;
    try {
      return JSON.parse(value || '{}');
    } catch (_) {
      return {};
    }
  }

  groupIdenticalItems(items = []) {
    const groups = new Map();
    items.forEach((item) => {
      const customizations = Array.isArray(item.customizations)
        ? item.customizations
        : (() => { try { return JSON.parse(item.customizations || '[]'); } catch (_) { return []; } })();
      const key = JSON.stringify({
        product: item.produto_id || item.product_id || item.produto_name || item.product_name,
        size: item.size_label || item.size || '',
        observation: String(item.observation || '').trim().toLowerCase(),
        options: (item.options || []).map((option) => option?.id || option?.name).filter(Boolean).sort(),
        customizations: customizations.map((group) => ({
          group: group.group_id || group.group_name || group.groupName,
          options: (group.selected_options || group.selectedOptions || [])
            .map((option) => option?.id || option?.name)
            .filter(Boolean)
            .sort()
        }))
      });
      const current = groups.get(key);
      if (current) {
        current.quantity += Number(item.quantity || 1);
        current.subtotal += Number(item.subtotal || 0);
      } else {
        groups.set(key, {
          ...item,
          quantity: Number(item.quantity || 1),
          subtotal: Number(item.subtotal || 0),
          customizations
        });
      }
    });
    return Array.from(groups.values());
  }

  shouldFilterWaiterBeverages(pedido, lanchonete, options = {}) {
    if (!options.automatic) return false;
    const config = this.getPrinterConfig(lanchonete);
    const isWaiterTableOrder = Boolean(pedido?.waiter_id) && (
      Boolean(pedido?.mesa_id)
      || String(pedido?.tipo_entrega || '').toUpperCase() === 'MESA'
      || String(pedido?.payment_method || '').toLowerCase() === 'mesa'
    );
    return config.waiter_kitchen_only === true && isWaiterTableOrder;
  }

  prepareAutomaticPrintOrder(pedido, lanchonete, options = {}) {
    if (!this.shouldFilterWaiterBeverages(pedido, lanchonete, options)) return pedido;
    const sourceItems = pedido?.items || pedido?.itens || [];
    const kitchenItems = sourceItems.filter((item) => this.isKitchenItem(item));
    return {
      ...pedido,
      items: kitchenItems,
      itens: kitchenItems,
      kitchen_only_print: true
    };
  }

  imprimirHtml(titulo, html) {
    const printJob = () => this.imprimirHtmlAgora(titulo, html);
    const result = this.printQueue.then(printJob, printJob);
    // Uma falha nao pode travar os proximos pedidos da fila.
    this.printQueue = result.catch(() => false);
    return result;
  }

  imprimirHtmlAgora(titulo, html) {
    return new Promise((resolve) => {
      if (!this.isPrintSupported) {
        console.error('Impressao nao suportada neste navegador');
        resolve(false);
        return;
      }

      document.getElementById('browser-print-style')?.remove();
      document.getElementById('browser-print-container')?.remove();

      const style = document.createElement('style');
      style.id = 'browser-print-style';
      style.textContent = `
        @media print {
          html, body {
            margin: 0 !important;
            padding: 0 !important;
            width: 100% !important;
            max-width: 100% !important;
            height: auto !important;
            min-height: 0 !important;
            overflow: visible !important;
            box-sizing: border-box !important;
          }
          body > *:not(#browser-print-container) { display: none !important; }
          #browser-print-container {
            display: block !important;
            position: static !important;
            margin: 0 !important;
            padding: 0 !important;
            width: 100% !important;
            max-width: 100% !important;
            height: auto !important;
            min-height: 0 !important;
            overflow: visible !important;
          }
          #browser-print-container, #browser-print-container * {
            visibility: visible !important;
            break-after: auto !important;
            box-sizing: border-box !important;
            max-width: 100% !important;
            overflow-wrap: anywhere !important;
            word-break: break-word !important;
          }
          #browser-print-container table {
            width: 100% !important;
            table-layout: fixed !important;
            border-collapse: collapse !important;
          }
          #browser-print-container td,
          #browser-print-container th {
            min-width: 0 !important;
            white-space: normal !important;
            overflow: visible !important;
          }
          /*
           * Usa a largura realmente informada pelo driver da impressora.
           * O padding fica dentro dos 100%, protegendo as duas bordas sem
           * deslocar ou alargar o cupom em bobinas de 58 mm e 80 mm.
           */
          #browser-print-container .thermal-receipt {
            width: 44mm !important;
            max-width: 44mm !important;
            margin: 0 3mm !important;
            padding: 1mm !important;
            font-size: 7.5px !important;
            line-height: 1.2 !important;
            font-weight: 800 !important;
            color: #000 !important;
            -webkit-print-color-adjust: exact !important;
            print-color-adjust: exact !important;
            overflow: visible !important;
          }
          #browser-print-container .thermal-receipt p,
          #browser-print-container .thermal-receipt td {
            font-size: 7.5px !important;
            line-height: 1.2 !important;
            font-weight: 800 !important;
            color: #000 !important;
          }
          #browser-print-container .thermal-receipt h2 { font-size: 11px !important; font-weight: 900 !important; color: #000 !important; }
          #browser-print-container .thermal-receipt h3 { font-size: 8.5px !important; font-weight: 900 !important; color: #000 !important; }
          #browser-print-container .thermal-receipt td:first-child {
            width: 62% !important;
            padding-right: 3px !important;
          }
          #browser-print-container .thermal-receipt td:not([colspan]):last-child {
            width: 38% !important;
            padding-left: 2px !important;
            text-align: right !important;
            font-size: 7px !important;
            overflow-wrap: anywhere !important;
            word-break: normal !important;
          }
          #browser-print-container .thermal-receipt td[colspan] {
            width: 100% !important;
          }
          #browser-print-container tr,
          #browser-print-container td,
          #browser-print-container p {
            break-inside: avoid !important;
            page-break-inside: avoid !important;
          }
          @page { margin: 0; size: 58mm auto; }
        }
        @media screen {
          #browser-print-container {
            position: fixed;
            left: -9999px;
            top: 0;
            width: 300px;
          }
        }
      `;

      const container = document.createElement('div');
      container.id = 'browser-print-container';
      container.setAttribute('aria-hidden', 'true');
      container.innerHTML = html;

      document.head.appendChild(style);
      document.body.appendChild(container);

      let finished = false;
      const cleanup = () => {
        style.remove();
        container.remove();
      };
      const finalizar = (ok) => {
        if (finished) return;
        finished = true;
        window.removeEventListener('afterprint', onAfterPrint);
        cleanup();
        resolve(ok);
      };

      const onAfterPrint = () => {
        window.removeEventListener('afterprint', onAfterPrint);
        finalizar(true);
      };

      window.addEventListener('afterprint', onAfterPrint);

      try {
        window.focus();
        const result = window.print();

        // No All Delivery Desktop, window.print devolve uma Promise com a
        // confirmacao do spooler do Windows. Esperamos essa confirmacao antes
        // de remover o cupom e iniciar o proximo trabalho da fila.
        if (result && typeof result.then === 'function') {
          result
            .then((desktopResult) => finalizar(desktopResult?.success !== false))
            .catch((error) => {
              console.error('Erro na impressao silenciosa:', error);
              finalizar(false);
            });
          return;
        }

        // Navegadores comuns nao retornam resultado de window.print. Nesse
        // caso, afterprint confirma o encerramento da caixa de impressao e o
        // tempo limite evita manter a fila bloqueada em navegadores antigos.
        setTimeout(() => finalizar(true), 3000);
      } catch (error) {
        console.error('Erro ao imprimir:', error);
        finalizar(false);
      }
    });
  }

  /**
   * Formata o cupom do pedido para impressão
   * @param {Object} pedido - Dados do pedido
   * @param {Object} lanchonete - Dados da lanchonete
   * @returns {string} HTML formatado
   */
  formatarCupom(pedido, lanchonete) {
    const data = new Date(pedido.created_at).toLocaleString('pt-BR');

    const paymentLabels = {
      money: 'DINHEIRO',
      pix: 'PIX',
      card: 'CARTAO',
      mercadopago: 'MERCADO PAGO',
      pickup: 'RETIRADA NO LOCAL',
      local: 'COMER NO LOCAL',
      delivery: 'PAGAR NA ENTREGA',
      mesa: 'MESA'
    };
    const paymentMethod = String(pedido.payment_method || '').trim().toLowerCase();
    const paymentLabel = paymentLabels[paymentMethod] || String(pedido.payment_method || 'NAO INFORMADO').toUpperCase();

    let itensHtml = '';
    let totalItens = 0;

    const items = pedido.items || pedido.itens || [];
    items.forEach((item, itemIndex) => {
      const itemTotal = Number(item.subtotal || (item.quantity * (item.unit_price || item.price)) || 0);
      totalItens += itemTotal;

      itensHtml += `
        <tr>
          <td colspan="2" style="border-top: ${itemIndex === 0 ? '2px solid #000' : '2px dashed #000'}; padding: 7px 0 3px; font-size: 12px; font-weight: 900;">ITEM ${itemIndex + 1}</td>
        </tr>
        <tr>
          <td style="width: 68%; padding-right: 4px; vertical-align: top; overflow-wrap: anywhere; word-break: break-word;">${escapeHtml(item.quantity)}x ${escapeHtml(item.produto_name || item.name || 'Produto')}</td>
          <td style="width: 32%; text-align: right; vertical-align: top; white-space: nowrap !important;">R$ ${itemTotal.toFixed(2)}</td>
        </tr>
      `;

      const itemObservation = String(item.observation || item.observations || '').trim();

      const customizations = Array.isArray(item.customizations)
        ? item.customizations
        : (() => {
            try { return JSON.parse(item.customizations || '[]'); } catch (_) { return []; }
          })();
      customizations.forEach((group) => {
        const optionNames = (group.selected_options || group.selectedOptions || [])
          .map((option) => String(option.name || '').trim())
          .filter(Boolean);

        if (optionNames.length > 0) {
          itensHtml += `
            <tr>
              <td colspan="2" style="font-size: 11px; color: #000; font-weight: bold; padding: 2px 0 2px 8px; overflow-wrap: anywhere; word-break: break-word;">
                ${escapeHtml(group.group_name || group.groupName || 'Opções')}: ${optionNames.map(escapeHtml).join(', ')}
              </td>
            </tr>
          `;
        }
      });
      if (itemObservation) {
        itensHtml += `
          <tr>
              <td colspan="2" style="font-size: 12px; font-weight: 900; padding: 5px 0 5px 8px; overflow-wrap: anywhere; word-break: break-word;">OBS: ${escapeHtml(itemObservation)}</td>
          </tr>
        `;
      }
    });

    const taxaEntrega = Number(pedido.delivery_fee || 0);
    const totalComEntrega = totalItens + taxaEntrega;
    const totalPedido = Number(pedido.total || pedido.total_amount || totalComEntrega);
    const precisaTroco = pedido.needs_change === true
      || pedido.needs_change === 1
      || pedido.needs_change === '1'
      || pedido.needs_change === 'true';
    const trocoPara = Number(pedido.change_amount || 0);
    const valorTroco = Math.max(0, trocoPara - totalPedido);

    return `
      <div class="thermal-receipt" style="font-family: 'Arial', 'Helvetica', sans-serif; font-size: 10px; line-height: 1.2; font-weight: bold; color: #000000; width: 100%; max-width: 100%; margin: 0; padding: 0; box-sizing: border-box; overflow-wrap: anywhere; word-break: break-word;">
        <!-- Cabeçalho -->
        <div style="text-align: center; margin-bottom: 12px;">
          <h2 style="margin: 0; font-size: 17px; font-weight: 900; color: #000;">${escapeHtml(lanchonete.name || 'LANCHONETE')}</h2>
          <p style="margin: 3px 0; font-size: 10px; font-weight: bold;">${escapeHtml(lanchonete.address || '')}</p>
          <p style="margin: 3px 0; font-size: 10px; font-weight: bold;">${escapeHtml(lanchonete.phone || '')}</p>
        </div>

        <!-- Separador -->
        <div style="border-top: 2px dashed #000; margin: 10px 0;"></div>

        <!-- Informações do pedido -->
        <div style="margin-bottom: 15px; font-size: 13px;">
          <p style="margin: 3px 0;"><strong>PEDIDO: #${escapeHtml(pedido.lanchonete_order_number || pedido.id)}</strong></p>
          <p style="margin: 3px 0;">DATA/HORA: ${data}</p>
          <p style="margin: 3px 0;">CLIENTE: ${escapeHtml(pedido.customer_name || 'Cliente')}</p>
          <p style="margin: 3px 0;">TEL: ${escapeHtml(pedido.customer_phone || '')}</p>
          <p style="margin: 3px 0;">END: ${escapeHtml(pedido.delivery_address || '')}</p>
          <p style="margin: 3px 0;">Nº CASA: ${escapeHtml(pedido.house_number || 'Não informado')}</p>
          ${pedido.reference ? `<p style="margin: 3px 0;">REF: ${escapeHtml(pedido.reference)}</p>` : ''}
        </div>

        <!-- Separador -->
        <div style="border-top: 2px dashed #000; margin: 10px 0;"></div>

        <!-- Itens do pedido -->
        <div style="margin-bottom: 15px;">
          <h3 style="margin: 5px 0; font-size: 14px; font-weight: 900;">ITENS DO PEDIDO:</h3>
          <table style="width: 100%; border-collapse: collapse; font-size: 13px; font-weight: bold;">
            ${itensHtml}
          </table>
        </div>

        <!-- Separador -->
        <div style="border-top: 2px dashed #000; margin: 10px 0;"></div>

        <!-- Totais -->
        <div style="margin-bottom: 15px; font-size: 13px;">
          <table style="width: 100%; border-collapse: collapse; font-weight: bold;">
            <tr>
              <td style="text-align: right;">SUBTOTAL:</td>
              <td style="text-align: right; width: 85px;">R$ ${totalItens.toFixed(2)}</td>
            </tr>
            ${taxaEntrega > 0 ? `
              <tr>
                <td style="text-align: right;">TAXA ENTREGA:</td>
                <td style="text-align: right;">R$ ${taxaEntrega.toFixed(2)}</td>
              </tr>
            ` : ''}
            <tr style="border-top: 2px solid #000;">
              <td style="text-align: right; font-size: 14px;"><strong>TOTAL:</strong></td>
              <td style="text-align: right; font-size: 14px; font-weight: 900;">R$ ${totalComEntrega.toFixed(2)}</td>
            </tr>
          </table>
        </div>

        <!-- Separador -->
        <div style="border-top: 2px dashed #000; margin: 10px 0;"></div>

        <!-- Pagamento e troco -->
        <div style="margin-bottom: 15px; font-size: 13px;">
          <p style="margin: 3px 0; font-weight: 900;">PAGAMENTO: ${escapeHtml(paymentLabel)}</p>
          ${paymentMethod === 'money' && precisaTroco && trocoPara > 0 ? `
            <p style="margin: 3px 0; font-weight: 900;">TROCO PARA: R$ ${trocoPara.toFixed(2)}</p>
            <p style="margin: 3px 0; font-weight: 900;">TROCO A DEVOLVER: R$ ${valorTroco.toFixed(2)}</p>
          ` : ''}
          ${paymentMethod === 'money' && !precisaTroco ? `
            <p style="margin: 3px 0;">NAO PRECISA DE TROCO</p>
          ` : ''}
        </div>

        <!-- Separador -->
        <div style="border-top: 2px dashed #000; margin: 10px 0;"></div>

        <!-- Status -->
        <div style="text-align: center; margin-bottom: 15px;">
          <p style="margin: 5px 0; font-weight: 900; font-size: 15px;">STATUS: ${escapeHtml(String(pedido.status || '').toUpperCase())}</p>
        </div>

        ${pedido.observation || pedido.observations ? `
          <div style="border-top: 2px dashed #000; margin: 10px 0;"></div>
          <div style="font-size: 13px; font-weight: 900; margin-bottom: 15px;">
            <p style="margin: 3px 0; font-size: 14px;">OBSERVACOES:</p>
            ${pedido.observation || pedido.observations ? `<p style="margin: 4px 0; overflow-wrap: anywhere; word-break: break-word;">GERAL: ${escapeHtml(pedido.observation || pedido.observations)}</p>` : ''}
          </div>
        ` : ''}

        <!-- Rodapé -->
        <div style="text-align: center; margin-top: 20px;">
          <p style="margin: 5px 0; font-size: 11px; font-weight: bold;">Obrigado pela preferência!</p>
          <p style="margin: 5px 0; font-size: 11px; font-weight: bold;">Volte sempre!</p>
        </div>
      </div>
    `;
  }

  /**
   * Imprime o cupom do pedido
   * @param {Object} pedido - Dados do pedido
   * @param {Object} lanchonete - Dados da lanchonete
   * @returns {Promise<boolean>}
   */
  async imprimirPedido(pedido, lanchonete, options = {}) {
    try {
      const printOrder = this.prepareAutomaticPrintOrder(pedido, lanchonete, options);
      const printItems = printOrder.items || printOrder.itens || [];
      // Um lancamento contendo apenas bebidas permanece no pedido/mesa, mas nao gasta papel.
      if (printOrder.kitchen_only_print && printItems.length === 0) {
        console.log(`Pedido #${pedido.id} contem somente bebidas; impressao automatica ignorada.`);
        return true;
      }

      const cupomHtml = this.formatarCupom(printOrder, lanchonete);
      const orderNum = pedido.lanchonete_order_number || pedido.id;
      const success = await this.imprimirHtml(`Cupom Pedido #${orderNum}`, cupomHtml);
      if (success) {
        console.log(`Cupom do pedido #${orderNum} enviado para impressao`);
      }
      return success;
      /*
      if (!this.isPrintSupported) {
        console.error('❌ Impressão não suportada neste navegador');
        return false;
      }

      // Criar janela de impressão
      const printWindow = window.open('', '_blank');
      
      if (!printWindow) {
        console.error('❌ Não foi possível abrir janela de impressão');
        return false;
      }

      // Gerar HTML do cupom
      const cupomHtml = this.formatarCupom(pedido, lanchonete);

      // Escrever HTML na janela
      printWindow.document.write(`
        <!DOCTYPE html>
        <html>
        <head>
          <title>Cupom Pedido #${escapeHtml(pedido.id)}</title>
          <style>
            @media print {
              body { margin: 0; padding: 0; }
              @page { margin: 5mm; }
            }
          </style>
        </head>
        <body>
          ${cupomHtml}
        </body>
        </html>
      `);

      printWindow.document.close();

      // Aguardar carregar e imprimir
      printWindow.onload = () => {
        setTimeout(() => {
          printWindow.print();
          printWindow.close();
        }, 500);
      };

      console.log(`🖨️ Cupom do pedido #${pedido.id} enviado para impressão`);
      return true;

      */
    } catch (error) {
      console.error('❌ Erro ao imprimir cupom:', error);
      return false;
    }
  }

  /**
   * Imprime cupom de teste
   * @param {Object} lanchonete - Dados da lanchonete
   * @returns {Promise<boolean>}
   */
  async imprimirTeste(lanchonete) {
    const pedidoTeste = {
      id: 'TESTE',
      created_at: new Date(),
      customer_name: 'CLIENTE TESTE',
      customer_phone: '87999999999',
      delivery_address: 'Endereço de Teste',
      house_number: '123',
      reference: 'Referência Teste',
      status: 'pendente',
      delivery_fee: 3.00,
      items: [
        {
          quantity: 1,
          produto_name: 'X-Burger Teste',
          unit_price: 15.00,
          observation: 'Sem cebola'
        },
        {
          quantity: 2,
          produto_name: 'Refrigerante Teste',
          unit_price: 5.00,
          observation: null
        }
      ]
    };

    return await this.imprimirPedido(pedidoTeste, lanchonete);
  }

  /**
   * Formata a conta da mesa para impressão
   * @param {Object} mesa - Dados da mesa
   * @param {Object} detalhes - Detalhes dos pedidos da mesa
   * @param {Object} lanchonete - Dados da lanchonete
   * @returns {string} HTML formatado
   */
  formatarContaMesa(mesa, detalhes, lanchonete) {
    const data = new Date().toLocaleString('pt-BR');
    let pedidosHtml = '';
    detalhes.orders.forEach(order => {
      pedidosHtml += `
        <tr>
          <td style="padding: 5px 0; border-bottom: 1px solid #000;">Pedido #${escapeHtml(order.lanchonete_order_number || order.id)}</td>
          <td style="text-align: right;">R$ ${Number(order.total).toFixed(2)}</td>
        </tr>
      `;

      this.groupIdenticalItems(order.items || order.itens || []).forEach((item, itemIndex) => {
        pedidosHtml += `
          <tr>
            <td colspan="2" style="border-top: ${itemIndex === 0 ? '1px solid #000' : '1px dashed #000'}; padding-top: 6px; font-size: 11px; font-weight: 900;">ITEM ${itemIndex + 1}</td>
          </tr>
          <tr>
            <td colspan="2" style="padding-top: 5px; font-size: 12px;">
              ${escapeHtml(item.quantity)}x ${escapeHtml(item.produto_name || item.product_name || `Produto #${item.produto_id}`)}
            </td>
          </tr>
        `;

        const customizations = Array.isArray(item.customizations)
          ? item.customizations
          : (() => {
              try { return JSON.parse(item.customizations || '[]'); } catch (_) { return []; }
            })();
        customizations.forEach((group) => {
          const optionNames = (group.selected_options || group.selectedOptions || [])
            .map((option) => String(option.name || '').trim())
            .filter(Boolean);
          if (optionNames.length > 0) {
            pedidosHtml += `
              <tr>
                <td colspan="2" style="padding-left: 10px; font-size: 11px;">
                  ${escapeHtml(group.group_name || group.groupName || 'Opcoes')}: ${optionNames.map(escapeHtml).join(', ')}
                </td>
              </tr>
            `;
          }
        });

        const itemObservation = String(item.observation || item.observations || '').trim();
        if (itemObservation) {
          pedidosHtml += `
            <tr>
            <td colspan="2" style="padding: 5px 0 5px 10px; font-size: 12px; font-weight: 900;">OBS: ${escapeHtml(itemObservation)}</td>
            </tr>
          `;
        }
      });
    });

    const pagamentosHtml = (detalhes.payments || []).map((payment) => `
      <tr>
        <td>Valor recebido</td>
        <td style="text-align: right;">- R$ ${Number(payment.amount || 0).toFixed(2)}</td>
      </tr>
    `).join('');

    return `
      <div class="thermal-receipt" style="font-family: 'Arial', 'Helvetica', sans-serif; font-size: 10px; line-height: 1.2; font-weight: bold; color: #000000; width: 100%; max-width: 100%; margin: 0; padding: 0; box-sizing: border-box; overflow-wrap: anywhere; word-break: break-word;">
        <!-- Cabeçalho -->
        <div style="text-align: center; margin-bottom: 12px;">
          <h2 style="margin: 0; font-size: 17px; font-weight: 900; color: #000;">${escapeHtml(lanchonete?.name || 'LANCHONETE')}</h2>
          <p style="margin: 3px 0; font-size: 10px; font-weight: bold;">${escapeHtml(lanchonete?.address || '')}</p>
          <p style="margin: 3px 0; font-size: 10px; font-weight: bold;">CONTA DA MESA</p>
        </div>

        <!-- Separador -->
        <div style="border-top: 2px dashed #000; margin: 10px 0;"></div>

        <!-- Informações da mesa -->
        <div style="margin-bottom: 15px; font-size: 13px;">
          <p style="margin: 3px 0;"><strong>MESA: #${escapeHtml(mesa.numero)}</strong></p>
          <p style="margin: 3px 0;">DATA/HORA: ${data}</p>
          <p style="margin: 3px 0;">CLIENTE: ${escapeHtml(mesa.customer_name || 'Consumidor')}</p>
        </div>

        <!-- Separador -->
        <div style="border-top: 2px dashed #000; margin: 10px 0;"></div>

        <!-- Pedidos -->
        <div style="margin-bottom: 15px;">
          <h3 style="margin: 5px 0; font-size: 14px; font-weight: 900;">RESUMO DE PEDIDOS:</h3>
          <table style="width: 100%; border-collapse: collapse; font-size: 13px; font-weight: bold;">
            ${pedidosHtml}
          </table>
        </div>

        <!-- Separador -->
        <div style="border-top: 2px dashed #000; margin: 10px 0;"></div>

        ${pagamentosHtml ? `
          <div style="margin-bottom: 15px;">
            <h3 style="margin: 5px 0; font-size: 14px; font-weight: 900;">PAGAMENTOS JA RECEBIDOS:</h3>
            <table style="width: 100%; border-collapse: collapse; font-size: 13px; font-weight: bold;">
              ${pagamentosHtml}
            </table>
          </div>
          <div style="border-top: 2px dashed #000; margin: 10px 0;"></div>
        ` : ''}

        <!-- Totais -->
        <div style="margin-bottom: 15px; font-size: 13px;">
          <table style="width: 100%; border-collapse: collapse; font-weight: bold;">
            <tr style="border-top: 2px solid #000;">
              <td style="text-align: right; font-size: 13px;"><strong>TOTAL CONSUMIDO:</strong></td>
              <td style="text-align: right; font-size: 14px; font-weight: 900;">R$ ${Number(detalhes.total_parcial || 0).toFixed(2)}</td>
            </tr>
            <tr>
              <td style="text-align: right; font-size: 13px;"><strong>TOTAL PAGO:</strong></td>
              <td style="text-align: right; font-size: 14px; font-weight: 900;">R$ ${Number(detalhes.total_paid || 0).toFixed(2)}</td>
            </tr>
            <tr style="border-top: 2px solid #000;">
              <td style="text-align: right; font-size: 15px;"><strong>SALDO A PAGAR:</strong></td>
              <td style="text-align: right; font-size: 16px; font-weight: 900;">R$ ${Number(detalhes.balance_due ?? detalhes.total_parcial ?? 0).toFixed(2)}</td>
            </tr>
          </table>
        </div>

        <!-- Rodapé -->
        <div style="text-align: center; margin-top: 20px;">
          <p style="margin: 5px 0; font-size: 11px; font-weight: bold;">Obrigado pela preferência!</p>
          <p style="margin: 5px 0; font-size: 11px; font-weight: bold;">Volte sempre!</p>
        </div>
      </div>
    `;
  }

  isKitchenItem(item) {
    const category = String(item?.categoria_name || item?.category || '')
      .normalize('NFD').replace(/[\u0300-\u036f]/g, '').toLowerCase();
    const name = String(item?.produto_name || item?.product_name || '')
      .normalize('NFD').replace(/[\u0300-\u036f]/g, '').toLowerCase();
    const beverageTerms = [
      'bebida', 'refrigerante', 'refri', 'coca', 'sukita', 'guarana',
      'fanta', 'sprite', 'pepsi', 'agua', 'suco', 'cerveja', 'vinho',
      'drink', 'energetico', 'skol', 'brahma', 'heineken', 'itaipava'
    ];
    return !beverageTerms.some((term) => category.includes(term) || name.includes(term));
  }

  formatarCozinhaMesa(mesa, detalhes, lanchonete) {
    const orders = (detalhes?.orders || []).map((order) => ({
      ...order,
      items: (order.items || order.itens || []).filter((item) => this.isKitchenItem(item))
    })).filter((order) => order.items.length > 0);

    const itemsHtml = orders.map((order) => `
      <div style="border-bottom: 2px dashed #000; padding: 7px 0;">
        <div style="font-size: 13px; margin-bottom: 5px;">PEDIDO #${escapeHtml(order.lanchonete_order_number || order.id)}</div>
        ${order.items.map((item, itemIndex) => {
          const customizations = Array.isArray(item.customizations)
            ? item.customizations
            : (() => { try { return JSON.parse(item.customizations || '[]'); } catch (_) { return []; } })();
          const options = customizations.map((group) => {
            const names = (group.selected_options || group.selectedOptions || []).map((option) => String(option.name || '').trim()).filter(Boolean);
            return names.length ? `<div style="padding-left: 10px; font-size: 12px;">${escapeHtml(group.group_name || group.groupName || 'Opcoes')}: ${names.map(escapeHtml).join(', ')}</div>` : '';
          }).join('');
          const itemObservation = String(item.observation || item.observations || '').trim();
          return `
            <div style="padding: 7px 0; border-top: ${itemIndex === 0 ? '2px solid #000' : '2px dashed #000'};">
              <div style="font-size: 12px; font-weight: 900;">ITEM ${itemIndex + 1}</div>
              <div style="font-size: 16px; font-weight: 900;">${escapeHtml(item.quantity)}x ${escapeHtml(item.produto_name || item.product_name || `Produto #${item.produto_id}`)}</div>
              ${options}
              ${itemObservation ? `<div style="padding: 5px 0 0 10px; font-size: 14px; font-weight: 900;">OBS: ${escapeHtml(itemObservation)}</div>` : ''}
            </div>`;
        }).join('')}
      </div>
    `).join('');

    return `
      <div class="thermal-receipt" style="font-family: Arial, Helvetica, sans-serif; font-size: 10px; width: 100%; max-width: 100%; line-height: 1.2; color: #000; font-weight: bold; box-sizing: border-box; overflow-wrap: anywhere; word-break: break-word;">
        <div style="text-align: center; border-bottom: 3px solid #000; padding-bottom: 8px;">
          <div style="font-size: 18px; font-weight: 900;">COZINHA</div>
        <div style="font-size: 20px; font-weight: 900;">MESA #${escapeHtml(mesa.numero)}</div>
        <div style="font-size: 12px;">${escapeHtml(lanchonete?.name || 'LANCHONETE')}</div>
          <div style="font-size: 11px;">${new Date().toLocaleString('pt-BR')}</div>
        <div style="font-size: 12px;">CLIENTE: ${escapeHtml(mesa.customer_name || 'Consumidor')}</div>
        </div>
        ${itemsHtml}
        <div style="text-align: center; font-size: 13px; margin-top: 10px;">*** PRODUCAO DA COZINHA ***</div>
      </div>`;
  }

  async imprimirCozinhaMesa(mesa, detalhes, lanchonete) {
    const hasKitchenItems = (detalhes?.orders || []).some((order) =>
      (order.items || order.itens || []).some((item) => this.isKitchenItem(item))
    );
    if (!hasKitchenItems) return { success: false, reason: 'no_kitchen_items' };
    const html = this.formatarCozinhaMesa(mesa, detalhes, lanchonete);
    const success = await this.imprimirHtml(`Cozinha Mesa #${mesa.numero}`, html);
    return { success };
  }

  /**
   * Imprime a conta da mesa
   * @param {Object} mesa - Dados da mesa
   * @param {Object} detalhes - Detalhes dos pedidos
   * @param {Object} lanchonete - Dados da lanchonete
   * @returns {Promise<boolean>}
   */
  async imprimirContaMesa(mesa, detalhes, lanchonete) {
    try {
      const html = this.formatarContaMesa(mesa, detalhes, lanchonete);
      return await this.imprimirHtml(`Conta Mesa #${mesa.numero}`, html);
      /*
      if (!this.isPrintSupported) return false;

      const printWindow = window.open('', '_blank');
      if (!printWindow) return false;

      const html = this.formatarContaMesa(mesa, detalhes, lanchonete);

      printWindow.document.write(`
        <!DOCTYPE html>
        <html>
        <head>
          <title>Conta Mesa #${escapeHtml(mesa.numero)}</title>
          <style>
            @media print { body { margin: 0; padding: 0; } @page { margin: 5mm; } }
          </style>
        </head>
        <body>${html}</body>
        </html>
      `);

      printWindow.document.close();
      printWindow.onload = () => {
        setTimeout(() => {
          printWindow.print();
          printWindow.close();
        }, 500);
      };

      return true;
      */
    } catch (error) {
      console.error('Erro ao imprimir conta da mesa:', error);
      return false;
    }
  }

  /**
   * Verifica se impressão é suportada
   * @returns {boolean}
   */
  isSuportado() {
    return this.isPrintSupported;
  }
}

const browserPrintService = new BrowserPrintService();
export default browserPrintService;
